/**
 * KpiBar — Cinta compacta de KPIs del día para HoyPage.
 * 7 tarjetas clickeables que también actúan como filtros de la lista.
 *
 * Cálculo LOCAL a partir de las reservas cargadas (rows). No consulta backend
 * — la data ya viene con precio_total, comision_valor, descuento_comercial,
 * saldo_pendiente, saldo_a_cargo_de, cortesias, etc.
 *
 * Layout responsivo:
 *   xs (<600):  2 col × 4 filas (última fila 1)
 *   sm (≥600):  4 col × 2 filas
 *   md (≥900):  7 col en una fila
 */
import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import TodayIcon from '@mui/icons-material/Today';
import GroupIcon from '@mui/icons-material/Group';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PaidIcon from '@mui/icons-material/Paid';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import { formatPrecio } from './format';

// Cada item define: label, value, subtítulo opcional, color (gradient/shadow),
// icono, y opcionalmente el filtro que activa al hacer click.
function computeKpis(rows) {
  let salidasSet = new Set();
  let paxTotal = 0;
  let netoPasadias = 0;
  let netoCharter = 0;
  let comisionesPorPagar = 0;
  let cxcCausadas = 0;
  let porCobrarMuelle = 0;
  let cortesias = 0;

  rows.forEach(r => {
    // Las canceladas se ven en la lista (chip rojo) pero NO deben inflar los
    // KPIs de facturación / operación. Alinea el KPI "Neto pasadías" con la
    // fuente contable (cierre de muelle) que también las excluye.
    if ((r.estado || '') === 'cancelada') return;

    const sid = r.salida_id;
    if (sid) salidasSet.add(sid);
    paxTotal += (r.adultos || 0) + (r.ninos || 0) + (r.bebes || 0);

    // Neto Entretur = PVP − descuento comercial − comisión total
    // Se separa por tipo de servicio: pasadía vs charter (alquiler).
    const precio = Number(r.precio_total || 0);
    const desc = Number(r.descuento_comercial || 0);
    const comision = Number(r.comision_valor || 0);
    const anticipada = Number(r.comision_anticipada || 0);
    const netoR = Math.max(precio - desc - comision, 0);
    const esCharter = r.tipo_reservacion === 'alquiler';
    if (esCharter) netoCharter += netoR;
    else netoPasadias += netoR;

    // Comisión por pagar — alineada con Finanzas/Cronograma:
    //   pendiente = max((com − descuento_comision) − anticipada − abonos, 0)
    //   liquidada con método real (no anticipo) → 0
    // El backend ya calcula el número correcto en `comision_por_pagar_calc`
    // (endpoint /turismo/reservaciones); acá lo usamos directo. El fallback
    // a la fórmula antigua es defensivo por si el endpoint no envía el campo
    // en algún caller viejo.
    if (r.agencia_id) {
      if (r.comision_por_pagar_calc !== undefined && r.comision_por_pagar_calc !== null) {
        comisionesPorPagar += Number(r.comision_por_pagar_calc) || 0;
      } else {
        comisionesPorPagar += Math.max(comision - anticipada, 0);
      }
    }

    // Distinguir CxC vs por cobrar en muelle según saldo_a_cargo_de
    const saldo = Number(r.saldo_pendiente || 0);
    if (saldo > 0) {
      if ((r.saldo_a_cargo_de || '') === 'agencia') cxcCausadas += saldo;
      else porCobrarMuelle += saldo;
    }

    // Cortesías: por cantidad de personas (no reservas)
    const cort = Number(r.cortesias) || 0;
    if (cort > 0 || r.forma_pago_agencia === 'cortesia') {
      cortesias += (cort || ((r.adultos || 0) + (r.ninos || 0)));
    }
  });

  return {
    salidas: salidasSet.size,
    pax: paxTotal,
    netoPasadias, netoCharter,
    comisionesPorPagar,
    cxcCausadas, porCobrarMuelle,
    cortesias,
  };
}

const ITEMS = [
  { key: 'salidas',           label: 'Salidas hoy',           icon: <TodayIcon />,          gradient: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)', shadow: 'rgba(14, 165, 233, 0.35)' },
  { key: 'pax',               label: 'Pasajeros',             icon: <GroupIcon />,          gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', shadow: 'rgba(16, 185, 129, 0.35)' },
  { key: 'netoPasadias',      label: 'Neto pasadías',         icon: <AttachMoneyIcon />,    gradient: 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)', shadow: 'rgba(13, 148, 136, 0.35)', isMoney: true },
  { key: 'netoCharter',       label: 'Neto charter',          icon: <DirectionsBoatIcon />, gradient: 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)', shadow: 'rgba(20, 184, 166, 0.35)', isMoney: true },
  { key: 'comisionesPorPagar',label: 'Comis. x pagar',        icon: <PaidIcon />,           gradient: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)', shadow: 'rgba(139, 92, 246, 0.35)', isMoney: true, filter: 'con_agencia' },
  { key: 'cxcCausadas',       label: 'CxC causadas',          icon: <CreditCardIcon />,     gradient: 'linear-gradient(135deg, #0891b2 0%, #0e7490 100%)', shadow: 'rgba(8, 145, 178, 0.35)',  isMoney: true, filter: 'cxc_agencia' },
  { key: 'porCobrarMuelle',   label: 'Por cobrar muelle',     icon: <LocalAtmIcon />,       gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', shadow: 'rgba(245, 158, 11, 0.35)', isMoney: true, filter: 'por_cobrar' },
  { key: 'cortesias',         label: 'Cortesías',             icon: <CardGiftcardIcon />,   gradient: 'linear-gradient(135deg, #ec4899 0%, #be185d 100%)', shadow: 'rgba(236, 72, 153, 0.35)', filter: 'cortesias' },
];

export default function KpiBar({ rows = [], activeFilter, onFilter }) {
  const kpis = useMemo(() => computeKpis(rows), [rows]);

  return (
    <Box sx={{
      display: 'grid',
      gridTemplateColumns: {
        xs: 'repeat(2, 1fr)',    // móvil: 2 col × 4 filas
        sm: 'repeat(4, 1fr)',    // tablet: 4 col × 2 filas
        md: 'repeat(4, 1fr)',    // laptop chico: 4 col × 2 filas (con montos legibles)
        lg: 'repeat(8, 1fr)',    // desktop: 8 col en una fila
      },
      gap: 0.75,
      mb: 1.25,
    }}>
      {ITEMS.map(it => {
        const raw = kpis[it.key];
        const value = it.isMoney ? formatPrecio(raw || 0) : (raw ?? '—');
        const clickable = !!it.filter && typeof onFilter === 'function';
        const isActive = clickable && activeFilter === it.filter;
        const handleClick = clickable
          ? () => onFilter(isActive ? 'todas' : it.filter)
          : undefined;

        return (
          <Box key={it.key}
            onClick={handleClick}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            onKeyDown={clickable ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick(); }
            } : undefined}
            sx={{
              position: 'relative',
              p: 0.85,
              borderRadius: 2,
              background: isActive ? 'rgba(240, 253, 250, 0.95)' : 'rgba(255, 255, 255, 0.85)',
              backdropFilter: 'blur(20px)',
              boxShadow: isActive
                ? `0 4px 12px ${it.shadow}, inset 0 1px 0 rgba(255,255,255,0.9)`
                : '0 2px 8px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.9)',
              border: isActive
                ? `1px solid ${it.shadow.replace('0.35', '0.6')}`
                : '1px solid rgba(255, 255, 255, 0.5)',
              overflow: 'hidden',
              cursor: clickable ? 'pointer' : 'default',
              transition: 'all 0.2s ease',
              userSelect: 'none',
              '&:hover': clickable ? {
                transform: 'translateY(-1px)',
                boxShadow: `0 4px 14px ${it.shadow}, inset 0 1px 0 rgba(255,255,255,0.9)`,
              } : undefined,
              '&:focus-visible': {
                outline: `2px solid ${it.shadow.replace('0.35', '0.8')}`,
                outlineOffset: 2,
              },
              '&::before': {
                content: '""', position: 'absolute', top: 0, left: 0, right: 0,
                height: isActive ? 3 : 2,
                background: it.gradient,
              },
            }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, lg: 0.5 } }}>
              {/* Ícono: en desktop (lg+, 8 columnas apretadas) lo ocultamos
                  para dejar todo el ancho al monto — con 8 tiles cabe hasta
                  ~145px cada una y los montos grandes ($10.730.000) necesitan
                  ese espacio. */}
              <Box sx={{
                width: 26, height: 26, borderRadius: 1.5,
                background: it.gradient,
                color: '#fff', display: { xs: 'flex', lg: 'none' },
                alignItems: 'center', justifyContent: 'center',
                boxShadow: `0 2px 6px ${it.shadow}`,
                '& svg': { fontSize: 15 },
                flexShrink: 0,
              }}>
                {it.icon}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{
                  // El label se abrevia con letterSpacing más ajustado y no wrap
                  fontSize: { xs: 9, lg: 8.5 },
                  color: '#64748b', fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: { xs: 0.4, lg: 0.2 },
                  lineHeight: 1.1,
                }} noWrap>
                  {it.label}
                </Typography>
                <Typography sx={{
                  // fontSize se reduce en lg (8 columnas) para que los montos
                  // grandes no se corten. Usamos fontFeatureSettings para dígitos
                  // tabulares (alineación perfecta entre tarjetas).
                  fontSize: { xs: 14, md: 13, lg: 12.5, xl: 13.5 },
                  fontWeight: 800, color: '#0f172a', lineHeight: 1.15,
                  fontFeatureSettings: '"tnum"',
                  fontVariantNumeric: 'tabular-nums',
                }} noWrap>
                  {value}
                </Typography>
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
